<template>
    <div v-if="getMadeToOrder" class="backorder">
        <div class="backorder-estimated-wrapper">
            <i class="fas fa-tape"/>
            <h2>
                Made to Order
                <nuxt-link v-if="showReturnsLink" :to="returnsLink" class="returns-link" @click.native="hideModal">
                    Check returns
                </nuxt-link>
            </h2>
        </div>
        <h3 v-if="getLeadTime" class="lead-time">{{ getLeadTime }}</h3>
    </div>

    <div class="backorder" v-else-if="getBackorderable">
        <div v-if="isPastBackorderableDate">
            Contact us for availability
        </div>
        <div v-else
             class="backorder-estimated-wrapper">
            <i class="far fa-calendar"></i>
            <h2>
                Available to preorder
                <nuxt-link v-if="showReturnsLink" :to="returnsLink" class="returns-link" @click.native="hideModal">
                    Check returns
                </nuxt-link>
            </h2>
            <h3 class="info" v-if="getBackorderableDate">
                Estimated availability: {{ getBackorderableDate }}
            </h3>
            <h3 class="backorder-date-info">
                <span>
                    Order now to secure your place in line. You will receive an email verifying the estimated restock
                    date within 5-7 business days.
                </span>
            </h3>
        </div>

        <br>

        <notification :notificationId="selectedVariant"
                      notificationType="Variant"
                      :initialNotification="notification"
                      v-if="!this.productionEnv">
        </notification>
    </div>

    <div class="backorder" v-else>
        <div v-if="getQuantity > 10">
            <h2 class="stock-info">
                <span class="available"/>
                In stock
                <nuxt-link v-if="showReturnsLink" :to="returnsLink" class="returns-link" @click.native="hideModal">
                    Check returns
                </nuxt-link>
            </h2>
        </div>
        <div v-else-if="getCustomMadeInfo !== null">
            <h2 class="stock-info">
                <span class="few"/>
                {{getCustomMadeInfo}}
                <nuxt-link v-if="showReturnsLink" :to="returnsLink" class="returns-link" @click.native="hideModal">
                    Check returns
                </nuxt-link>
            </h2>
        </div>
        <div v-else-if="getQuantity <= 10 && getQuantity > 0">
            <h2 class="stock-info">
                <span class="few"/>
                In stock <span>(only {{getQuantity}} available)</span>
                <nuxt-link v-if="showReturnsLink" :to="returnsLink" class="returns-link" @click.native="hideModal">
                    Check returns
                </nuxt-link>
            </h2>
        </div>
        <div v-else>
            <h2 class="stock-info">
                <span class="out-of"/>
                Out of stock
                <nuxt-link v-if="showReturnsLink" :to="returnsLink" class="returns-link" @click.native="hideModal">
                    Check returns
                </nuxt-link>
            </h2>
        </div>
    </div>
</template>

<script>
    import Notification from "@/components/Notification";
    import moment from 'moment'

    export default {
        name: "product-backorder",
        components: {
            Notification,
        },
        data: function () {
            return {
                notification: { show: true }
            }
        },
        props: {
            getBackorderable: {
                type: Boolean,
                default: false
            },
            getBackorderableDate: {
                type: String,
                default: null
            },
            selectedVariant: {
                type: String,
                default: null
            },
            getQuantity: {
                type: Number,
                default: 0
            },
            getCustomMadeInfo: {
                type: String
            },
            getMadeToOrder: {
                type: Boolean,
                default: false
            },
            getLeadTime: {
                type: String,
                default: null
            },
            getSlug: {
                type: String,
                default: null
            },
            showReturnsLink: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            isPastBackorderableDate () {
                const date = moment.utc(this.getBackorderableDate, 'MMM D, YYYY')

                if (!date.isValid()) {
                    return false
                }

                const currentDate = moment()
                const isGreaterThan2Weeks = currentDate.diff(date, 'weeks')

                return isGreaterThan2Weeks >= 2
            },
            productionEnv() {   // global method in mixin
                return process.env.PRODUCTION
            },
            returnsLink () {
                return {
                    name: 'products-slug',
                    params: {
                        slug: this.getSlug,
                    },
                    hash: '#returns'
                }
            }
        },
        methods: {
            hideModal () {
                this.$store.dispatch('modal/hideModal')
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '@/assets/stylesheets/unstylished-headers.scss';
    @import '@/assets/stylesheets/variables.scss';

    h2, h3 {
      font-family: OpenSans !important;
    }

    h2 {
      font-size: 1.6rem !important;
    }

    h3 {
      font-size: 1.4rem !important;
    }

    .backorder {
        margin-top: 15px;
        font-size: 1.7rem;
        letter-spacing: 0.41px;
        color: #212121;

        .backorder-estimated-wrapper {
            display: grid;
            grid-template-columns: auto 1fr;
            grid-row-gap: 10px;

            i {
                grid-row: 1;
                grid-column: 1;
                font-size: 2.2rem;
                margin-right: 10px;
            }

            .info {
                grid-row: 2;
                grid-column: 2;
                font-style: italic;
            }

            .backorder-date-info {
                grid-column: 1 / 3;
                color: $color-gray-dark;
                display: grid;
                grid-template-columns: auto auto;
                font-size: 1.6rem;

                i {
                    font-size: 3.4rem;
                    margin-top: 6px;
                }
            }
        }

        .lead-time {
            font-weight: normal;
            margin-bottom: 5px;
        }

        .product-notification {
            padding-bottom: 20px;
        }

        .stock-info {
            margin-bottom: 25px;

            .available, .few, .out-of {
                float: left;
                margin-right: 7px;
                width: 23px;
                height: 23px;
                border-radius: 50%;
            }

            .available {
                background-color: #3fb255;
            }

            .few {
                background-color: #d97638;
            }

            .out-of {
                background-color: red;
            }

            span {
                font-style: italic;
            }
        }

        .returns-link {
            display: inline-block;
            color: #428bca;
            text-decoration: underline;
            margin-left: 10px;
        }
    }
</style>
